/**
 * FocusTodayCard
 * AI-curated list of 3-5 priority items for today
 * Combines tasks, weaknesses, and study plan activities
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useFocusTodayItems, type FocusTodayItem } from '@/hooks/useFocusTodayItems';
import { useCompleteTask, useDeleteTask } from '@/hooks/use-tasks';
import { useLearningProfileContext } from '@/contexts/LearningProfileContext';
import {
  Target,
  CheckCircle2,
  Trash2,
  Clock,
  Brain,
  BookOpen,
  Calendar,
  Sparkles,
  ChevronRight,
  ListTodo,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface FocusTodayCardProps {
  className?: string;
  maxItems?: number;
}

export function FocusTodayCard({ className, maxItems = 5 }: FocusTodayCardProps) {
  const navigate = useNavigate();
  const { triggerOnboarding } = useLearningProfileContext();
  const { data: focusItems = [], isLoading } = useFocusTodayItems(maxItems);
  const completeTask = useCompleteTask();
  const deleteTask = useDeleteTask();
  const [deleteTaskId, setDeleteTaskId] = useState<string | null>(null);

  const handleComplete = (item: FocusTodayItem) => {
    if (item.taskId) {
      completeTask.mutate({ taskId: item.taskId });
    }
  };

  const handleDelete = () => {
    if (deleteTaskId) {
      deleteTask.mutate(deleteTaskId);
      setDeleteTaskId(null);
    }
  };

  const handleAction = (item: FocusTodayItem) => {
    // Handle special action: prefix for triggering modals/actions
    if (item.actionUrl.startsWith('action:')) {
      const action = item.actionUrl.replace('action:', '');
      if (action === 'triggerOnboarding') {
        triggerOnboarding();
      }
      return;
    }
    navigate(item.actionUrl);
  };

  const getItemIcon = (type: FocusTodayItem['type']) => {
    switch (type) {
      case 'task':
        return CheckCircle2;
      case 'diagnostic':
        return Brain;
      case 'weakness':
        return Target;
      case 'exam_prep':
        return Calendar;
      case 'milestone':
        return Sparkles;
      default:
        return BookOpen;
    }
  };

  const getPriorityStyles = (priority: FocusTodayItem['priority']) => {
    switch (priority) {
      case 'urgent':
        return 'border-l-red-500 bg-red-50/50 dark:bg-red-950/20';
      case 'high':
        return 'border-l-amber-500 bg-amber-50/50 dark:bg-amber-950/20';
      case 'normal':
        return 'border-l-blue-300 bg-blue-50/30 dark:bg-blue-950/10';
      default:
        return 'border-l-gray-300 dark:border-l-gray-600';
    }
  };

  const getPriorityBadge = (priority: FocusTodayItem['priority']) => {
    switch (priority) {
      case 'urgent':
        return <Badge variant="destructive" className="text-xs">Urgent</Badge>;
      case 'high':
        return <Badge className="text-xs bg-amber-500 dark:bg-amber-600">High</Badge>;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <Skeleton className="h-5 w-28" />
            <Skeleton className="h-4 w-16" />
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-lg border-l-4 border-l-gray-200 dark:border-l-gray-700">
              <Skeleton className="h-4 w-4 rounded" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  // Don't show card if no items
  if (focusItems.length === 0) {
    return (
      <Card className={cn('border-dashed', className)}>
        <CardContent className="flex flex-col items-center justify-center py-6">
          <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-3">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
          </div>
          <p className="font-medium text-sm">You're all caught up!</p>
          <p className="text-xs text-muted-foreground mt-1">
            Check the Tasks tab for scheduled activities.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <ListTodo className="h-4 w-4 text-primary" />
            Focus Today
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs"
            onClick={() => navigate('/dashboard/tasks')}
          >
            View all
            <ChevronRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {focusItems.map((item) => {
          const ItemIcon = getItemIcon(item.type);
          return (
            <div
              key={item.id}
              role="button"
              tabIndex={0}
              aria-label={`${item.title}${item.description ? `: ${item.description}` : ''}`}
              className={cn(
                'flex items-center gap-3 p-3 rounded-lg border-l-4 transition-colors',
                'hover:bg-muted/50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                getPriorityStyles(item.priority)
              )}
              onClick={() => handleAction(item)}
              onKeyDown={(e) => e.key === 'Enter' && handleAction(item)}
            >
              <div className="shrink-0">
                <ItemIcon className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium truncate">{item.title}</p>
                  {getPriorityBadge(item.priority)}
                  {item.dueToday && (
                    <Badge variant="outline" className="text-xs">
                      <Clock className="h-3 w-3 mr-1" />
                      Today
                    </Badge>
                  )}
                </div>
                {item.description && (
                  <p className="text-xs text-muted-foreground truncate mt-0.5">
                    {item.description}
                  </p>
                )}
              </div>
              {/* Action buttons for tasks */}
              {item.taskId && (
                <div
                  className="flex items-center gap-2"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleComplete(item)}
                    aria-label={`Mark "${item.title}" as complete`}
                    className="h-10 w-10 md:h-8 md:w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-950/30"
                  >
                    <CheckCircle2 className="h-5 w-5 md:h-4 md:w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteTaskId(item.taskId!)}
                    aria-label={`Delete "${item.title}"`}
                    className="h-10 w-10 md:h-8 md:w-8 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-5 w-5 md:h-4 md:w-4" />
                  </Button>
                </div>
              )}
              {/* Navigate arrow for non-task items */}
              {!item.taskId && (
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
            </div>
          );
        })}
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTaskId} onOpenChange={(open) => !open && setDeleteTaskId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this task? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export default FocusTodayCard;
